/**
 * Runtime configuration for CloudCharge Go.
 *
 * Precedence, highest first:
 *   1. `window.__WEB_CONFIG__` — an optional `/config.js` served next to the build,
 *      rendered from the WEB_* environment by docker-entrypoint.d/40-web-config.sh
 *      when the container starts. Not part of the bundle, so one image serves every
 *      environment.
 *   2. `VITE_*` build-time variables — local development (`.env`, `.env.local`).
 *   3. The defaults below.
 *
 * Import `config` from './utils/config' rather than reading `import.meta.env` in app
 * code, so a setting can be changed without rebuilding.
 */

export type BrandKey = 'cloudcharge' | 'defa' | 'base';

export interface GoConfig {
    /** cc-go REST base, e.g. https://dev.cloudcharge.se/services/ccgo */
    apiUrl: string;
    /** Selects the theme and logo; a key into themesMap / logos, not an import path. */
    brandKey: BrandKey;
    /** Sentry DSN. Undefined disables reporting. */
    sentryDsn?: string;
    /** Stripe publishable (browser) key. Test key in non-prod, live key in prod. */
    stripePublishableKey: string;
}

const DEFAULTS: GoConfig = {
    apiUrl: 'https://prod.cloudcharge.se/services/ccgo',
    brandKey: 'cloudcharge',
    sentryDsn: undefined,
    stripePublishableKey: '',
};

// Vite substitutes these statically, so each variable has to be spelled out;
// `import.meta.env[name]` would not be replaced.
function fromBuildEnv(): Partial<GoConfig> {
    return {
        apiUrl: import.meta.env.VITE_CC_GO_API_URL,
        brandKey: import.meta.env.VITE_BRAND_KEY,
        sentryDsn: import.meta.env.VITE_SENTRY_DSN,
        stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
    };
}

function fromWindow(): Partial<GoConfig> {
    if (typeof window === 'undefined') return {};
    const injected = window.__WEB_CONFIG__;
    return injected && typeof injected === 'object' ? injected : {};
}

// Drop unset values so they don't shadow a lower-priority source: the entrypoint
// omits empty variables, but a build env may still inline them as '' or undefined.
function omitUnset<T extends object>(value: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(value).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ) as Partial<T>;
}

export const config: GoConfig = {
    ...DEFAULTS,
    ...omitUnset(fromBuildEnv()),
    ...omitUnset(fromWindow()),
};

export default config;
